/**
 * feat-235 — the Tracks tab's LEFT pane: the track list.
 *
 * Same 280px width, same search box, and the same ⠿-on-hover HTML5 drag as
 * `MasterDetailTree`'s navigator, because it does the same job: pick a thing,
 * and reorder the things. Track order is load-bearing — D8 makes "today's
 * lesson" come from the lowest-sorted unlocked track, so the author has to be
 * able to see and change it.
 *
 * `MasterDetailTree` is NOT reused and NOT restructured (spec, human decision):
 * with a lesson in several tracks, nesting lessons under tracks renders the same
 * lesson as several rows and every selection / drag / expand behaviour then needs
 * a rule for which copy the author meant.
 */

import React, { useState } from 'react';
import type { LearnTrack } from '../../../../services/quizContentAdminService';
import { QUIZ_TERMS } from '../../quizTerminology';
import { TrackSwatch } from './TrackSwatch';
import { moveBefore } from './trackGraph';

/** Inline create, mirroring `MasterDetailTree`'s InlineCreate. */
const InlineCreate: React.FC<{
  placeholder: string;
  onCancel: () => void;
  onCommit: (title: string) => Promise<void>;
}> = ({ placeholder, onCancel, onCommit }) => {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const commit = async () => {
    const title = value.trim();
    if (!title || busy) return;
    setBusy(true);
    try {
      await onCommit(title);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5">
      <input
        autoFocus
        value={value}
        disabled={busy}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') onCancel();
        }}
        className="flex-1 min-w-0 text-sm bg-white border border-softBorder rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-coral/40"
      />
      <button
        type="button"
        onClick={commit}
        disabled={busy || !value.trim()}
        className="text-xs text-coral font-semibold px-1.5 py-1 rounded hover:bg-coral-light disabled:opacity-40"
      >
        Add
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="text-xs text-inkMuted px-1 py-1 rounded hover:bg-gray-100"
      >
        ✕
      </button>
    </div>
  );
};

export interface TrackListProps {
  tracks: LearnTrack[];
  selectedTrackId: string | null;
  onSelectTrack: (trackId: string) => void;
  search: string;
  onSearch: (q: string) => void;
  onCreateTrack: (title: string) => Promise<void>;
  onReorderTracks: (orderedTrackIds: string[]) => void;
  /** False on a tenant where creating a track would LAUNDER a D2 violation —
   *  see `DefaultTrackAlert`. The control is removed rather than disabled: a
   *  greyed-out primary action on an empty screen reads as "try again later",
   *  and the banner beside it already says what to do instead. */
  canCreate?: boolean;
}

export const TrackList: React.FC<TrackListProps> = ({
  tracks,
  selectedTrackId,
  onSelectTrack,
  search,
  onSearch,
  onCreateTrack,
  onReorderTracks,
  canCreate = true,
}) => {
  const [adding, setAdding] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);

  const q = search.trim().toLowerCase();
  const visible = q ? tracks.filter((t) => (t.title || '').toLowerCase().includes(q)) : tracks;

  const reorder = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const ids = tracks.map((t) => t.id);
    const next = moveBefore(ids, dragId, targetId);
    if (next !== ids) onReorderTracks(next);
  };

  return (
    <aside className="border-r border-softBorder bg-cream/60 flex flex-col min-h-0 overflow-hidden">
      <div className="p-3 border-b border-softBorder shrink-0">
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={`Find a ${QUIZ_TERMS.track.singular}…`}
            aria-label={`Find a ${QUIZ_TERMS.track.singular}`}
            className="w-full text-sm bg-white border border-softBorder rounded-full pl-9 pr-3 py-2 placeholder:text-inkMuted/70 focus:outline-none focus:ring-2 focus:ring-coral/40"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-inkMuted/60 text-sm">⌕</span>
        </div>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto p-2 text-sm">
        {visible.map((track) => {
          const selected = track.id === selectedTrackId;
          return (
            <div
              key={track.id}
              role="button"
              tabIndex={0}
              aria-current={selected ? 'true' : undefined}
              data-testid={`track-row-${track.id}`}
              draggable
              onDragStart={() => setDragId(track.id)}
              onDragEnd={() => setDragId(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => reorder(track.id)}
              onClick={() => onSelectTrack(track.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectTrack(track.id);
                }
              }}
              className={`group flex items-center gap-1.5 rounded-lg px-2 py-2 cursor-pointer mt-0.5 ${
                selected ? 'bg-coral-light text-coral' : 'text-ink hover:bg-gray-100'
              } ${track.active ? '' : 'opacity-60'}`}
            >
              <span
                className={`opacity-0 group-hover:opacity-100 transition-opacity text-xs select-none cursor-grab ${
                  selected ? 'text-coral/40' : 'text-inkMuted/50'
                }`}
              >
                ⠿
              </span>
              <TrackSwatch
                title={track.title}
                imageKey={track.image_s3_key}
                testId={`track-row-swatch-${track.id}`}
                className="w-6 h-6 rounded-md border border-softBorder"
                monogramClassName="text-[9px]"
              />
              <span className={`flex-1 truncate ${selected ? 'font-bold' : 'font-semibold'}`}>
                {track.title || '(untitled)'}
              </span>
              <span className="text-[11px] text-inkMuted tabular-nums">{track.lesson_count}</span>
              {track.is_default && (
                <span className="bg-preview-light text-preview text-[10px] font-bold rounded-full px-1.5 py-0.5">
                  Default
                </span>
              )}
              {!track.active && (
                <span className="bg-gray-100 text-inkMuted text-[10px] font-bold rounded-full px-1.5 py-0.5">
                  Hidden
                </span>
              )}
              {track.access_mode === 'prereq' && (
                <span
                  title="Unlocks after other tracks are finished"
                  className="bg-cream-dark text-inkMuted text-[10px] font-bold rounded-full px-1.5 py-0.5"
                >
                  🔒
                </span>
              )}
            </div>
          );
        })}

        {visible.length === 0 && q !== '' && (
          <div className="text-xs text-inkMuted px-2 py-4 text-center">No matches for “{search}”.</div>
        )}
      </nav>

      {/* NOT RENDERED, not `hidden`: a Tailwind class is not applied CSS in a
          jsdom test, so a class-hidden button is still in the accessibility tree
          and still clickable — a suppression that only looks like one. */}
      {canCreate && (
        <div className="p-2 border-t border-softBorder shrink-0">
          {adding ? (
            <InlineCreate
              placeholder={`New ${QUIZ_TERMS.track.singular} title`}
              onCancel={() => setAdding(false)}
              onCommit={async (title) => {
                await onCreateTrack(title);
                setAdding(false);
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="w-full text-coral text-sm font-semibold rounded-lg px-2 py-2 hover:bg-coral-light text-left"
            >
              + {QUIZ_TERMS.track.singular}
            </button>
          )}
        </div>
      )}
    </aside>
  );
};

export default TrackList;
